import type { ReactNode } from 'react';
import { cx } from '../internal/cx';

/**
 * Casca das telas de autenticação (§38 do mandato R-UX01).
 *
 * Regra estrutural: **sem Sidebar**. Login não é aplicação — é a porta. Reaproveitar o
 * AppShell aqui daria ao usuário deslogado a moldura de um produto que ele ainda não
 * acessou, com navegação que não leva a lugar nenhum.
 *
 * A coluna de apoio (`aside`) é opcional e some no mobile: ela existe para dar respaldo
 * institucional em telas largas, não para carregar informação necessária. Se o conteúdo
 * dela fosse necessário, esconder no celular seria perda de informação — por isso ela
 * recebe conteúdo de reforço, e o `main` continua completo sozinho.
 */
export interface AuthShellProps {
  /** Marca no topo — normalmente um `<BrandMark />`. */
  brand?: ReactNode;
  /** Título da tela. Vira o `<h1>` da página: a casca é dona do nível do heading. */
  title: string;
  /** Uma frase de contexto abaixo do título. */
  description?: ReactNode;
  /** O formulário. */
  children: ReactNode;
  /** Rodapé do cartão — links auxiliares (ex.: "usar outro e-mail"). */
  footer?: ReactNode;
  /** Coluna de apoio, só em telas largas. Nunca informação essencial. */
  aside?: ReactNode;
  className?: string;
}

export function AuthShell({
  brand,
  title,
  description,
  children,
  footer,
  aside,
  className,
}: AuthShellProps) {
  return (
    <div className={cx('argink-auth-shell', Boolean(aside) && 'argink-auth-shell--split', className)}>
      <main className="argink-auth-shell__main">
        <div className="argink-auth-shell__card">
          {brand ? <div className="argink-auth-shell__brand">{brand}</div> : null}
          <header className="argink-auth-shell__header">
            <h1 className="argink-auth-shell__title">{title}</h1>
            {description ? (
              <p className="argink-auth-shell__description">{description}</p>
            ) : null}
          </header>
          <div className="argink-auth-shell__body">{children}</div>
          {footer ? <footer className="argink-auth-shell__footer">{footer}</footer> : null}
        </div>
      </main>
      {aside ? (
        /* `aria-hidden` não: o conteúdo é legítimo para quem usa leitor de tela em
         * desktop. Ele some por CSS no mobile, e como é reforço, não há perda. */
        <aside className="argink-auth-shell__aside">{aside}</aside>
      ) : null}
    </div>
  );
}
